import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  registerUser,
  assignableRolesUser,
  departmentsUser,
  fetchUsers,
  updateUser,
  deleteUser,
  toggleUserStatus,
} from "../services/api";
import { setAlert } from "../store/slices/alertSlice";
import { Modal, Pagination } from "react-bootstrap";
import {
  buildUsername,
  getEmptyUserForm,
  GENDER_OPTIONS,
  mapUserToForm,
  validateUserForm,
} from "../utils/userForm";
import { getUserDisplayName } from "../utils/userDisplay";
import MasterListToolbar from "./MasterListToolbar";
import { useDebouncedSearch } from "../hooks/useDebouncedSearch";

const PAGE_SIZE = 10;

const getFieldClass = (errors, field) =>
  `form-control${errors[field] ? " is-invalid" : ""}`;

const getSelectClass = (errors, field) =>
  `form-select${errors[field] ? " is-invalid" : ""}`;

const UserFormFields = ({
  formData,
  fieldErrors,
  editingUserId,
  roles,
  departments,
  onChange,
  currentUserId,
}) => (
  <>
    <div className="row g-3">
      <div className="col-md-6">
        <label className="form-label">
          First Name <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          className={getFieldClass(fieldErrors, "firstName")}
          name="firstName"
          value={formData.firstName}
          onChange={onChange}
        />
        {fieldErrors.firstName && (
          <div className="invalid-feedback d-block">{fieldErrors.firstName}</div>
        )}
      </div>

      <div className="col-md-6">
        <label className="form-label">
          Last Name <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          className={getFieldClass(fieldErrors, "lastName")}
          name="lastName"
          value={formData.lastName}
          onChange={onChange}
        />
        {fieldErrors.lastName && (
          <div className="invalid-feedback d-block">{fieldErrors.lastName}</div>
        )}
      </div>

      <div className="col-md-6">
        <label className="form-label">Username</label>
        <input
          type="text"
          className="form-control"
          name="username"
          value={formData.username}
          disabled
          readOnly
        />
        <div className="form-text">Generated from first and last name. Email is the unique login identifier.</div>
      </div>

      <div className="col-md-6">
        <label className="form-label">
          Email <span className="text-danger">*</span>
        </label>
        <input
          type="email"
          className={getFieldClass(fieldErrors, "email")}
          name="email"
          value={formData.email}
          onChange={onChange}
        />
        {fieldErrors.email && (
          <div className="invalid-feedback d-block">{fieldErrors.email}</div>
        )}
      </div>

      <div className="col-md-6">
        <label className="form-label">Designation</label>
        <input
          type="text"
          className="form-control"
          name="designation"
          value={formData.designation}
          onChange={onChange}
          placeholder="Optional"
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Gender</label>
        <select
          className="form-select"
          name="gender"
          value={formData.gender}
          onChange={onChange}
        >
          {GENDER_OPTIONS.map((option) => (
            <option key={option.value || "none"} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="col-md-6">
        <label className="form-label">
          Role <span className="text-danger">*</span>
        </label>
        <select
          className={getSelectClass(fieldErrors, "role")}
          name="role"
          value={formData.role}
          onChange={onChange}
        >
          <option value="" disabled>
            Select Role
          </option>
          {roles.map((role) => (
            <option key={role._id} value={role._id}>
              {role.roleName}
            </option>
          ))}
        </select>
        {fieldErrors.role && (
          <div className="invalid-feedback d-block">{fieldErrors.role}</div>
        )}
      </div>

      <div className="col-md-6">
        <label className="form-label">
          Department <span className="text-danger">*</span>
        </label>
        <select
          className={getSelectClass(fieldErrors, "department")}
          name="department"
          value={formData.department}
          onChange={onChange}
        >
          <option value="" disabled>
            Select Department
          </option>
          {departments.map((department) => (
            <option key={department._id} value={department._id}>
              {department.departmentName} ({department.departmentCode})
            </option>
          ))}
        </select>
        {fieldErrors.department && (
          <div className="invalid-feedback d-block">{fieldErrors.department}</div>
        )}
      </div>

      <div className="col-md-6">
        <label className="form-label">
          Password {!editingUserId && <span className="text-danger">*</span>}
        </label>
        <input
          type="password"
          className={getFieldClass(fieldErrors, "password")}
          name="password"
          value={formData.password}
          onChange={onChange}
          placeholder={editingUserId ? "Leave blank to keep current password" : ""}
          autoComplete="new-password"
        />
        {fieldErrors.password && (
          <div className="invalid-feedback d-block">{fieldErrors.password}</div>
        )}
      </div>

      <div className="col-md-6">
        <label className="form-label">
          Confirm Password {!editingUserId && <span className="text-danger">*</span>}
        </label>
        <input
          type="password"
          className={getFieldClass(fieldErrors, "confirmPassword")}
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={onChange}
          placeholder={editingUserId ? "Required only when changing password" : ""}
          autoComplete="new-password"
        />
        {fieldErrors.confirmPassword && (
          <div className="invalid-feedback d-block">{fieldErrors.confirmPassword}</div>
        )}
      </div>

      {editingUserId && editingUserId !== currentUserId && (
        <div className="col-12">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="user-is-active"
              name="isActive"
              checked={formData.isActive}
              onChange={(e) => onChange({ target: { name: "isActive", value: e.target.checked } })}
            />
            <label className="form-check-label" htmlFor="user-is-active">
              Account active (user can log in)
            </label>
          </div>
        </div>
      )}
    </div>
  </>
);

const RegisterForm = () => {
  const [formData, setFormData] = useState(getEmptyUserForm());
  const [fieldErrors, setFieldErrors] = useState({});
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { searchInput, setSearchInput, appliedSearch, clearSearch } = useDebouncedSearch();

  const dispatch = useDispatch();
  const currentUserId = useSelector((state) => state.auth.user?.id);
  const currentUserRole = useSelector((state) => state.auth.user?.role);
  const isFullAdmin = currentUserRole === "Admin";

  const canManageUser = (user) =>
    isFullAdmin || user.role?.roleName !== "Admin";

  const visibleUsers = isFullAdmin
    ? users
    : users.filter((user) => user.role?.roleName !== "Admin");

  const loadUsers = useCallback(async (page, search) => {
    setLoading(true);
    try {
      const response = await fetchUsers(page, PAGE_SIZE, search);
      setUsers(response.data.users);
      setTotalPages(response.data.totalPages || 1);
      setTotalUsers(response.data.totalUsers || 0);
      setCurrentPage(Number(response.data.currentPage || page));
    } catch (error) {
      dispatch(setAlert({ message: "Error loading users", type: "danger" }));
    }
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [rolesResponse, departmentsResponse] = await Promise.all([
          assignableRolesUser(),
          departmentsUser(),
        ]);
        setRoles(rolesResponse.data);
        setDepartments(departmentsResponse.data);
      } catch (error) {
        dispatch(setAlert({ message: "Error fetching data", type: "danger" }));
      }
    };
    fetchInitialData();
  }, [dispatch]);

  useEffect(() => {
    loadUsers(currentPage, appliedSearch);
  }, [currentPage, appliedSearch, loadUsers]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedSearch]);

  const handleClearSearch = () => {
    clearSearch();
    setCurrentPage(1);
  };

  const updateForm = (updates) => {
    setFormData((prev) => {
      const next = { ...prev, ...updates };

      if ("firstName" in updates || "lastName" in updates) {
        next.username = buildUsername(next.firstName, next.lastName);
      }

      return next;
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : value;
    updateForm({ [name]: nextValue });

    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const getDisplayName = (user) => getUserDisplayName(user);

  const closeFormModal = () => {
    setShowFormModal(false);
    setFormData(getEmptyUserForm());
    setFieldErrors({});
    setEditingUserId(null);
  };

  const openAddModal = () => {
    setFormData(getEmptyUserForm());
    setFieldErrors({});
    setEditingUserId(null);
    setShowFormModal(true);
  };

  const openEditModal = (user) => {
    if (!canManageUser(user)) {
      dispatch(setAlert({
        message: "You cannot edit an administrator account.",
        type: "danger",
      }));
      return;
    }

    setFormData(mapUserToForm(user));
    setFieldErrors({});
    setEditingUserId(user._id);
    setShowFormModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateUserForm(formData, { isEdit: Boolean(editingUserId) });
    if (Object.keys(validationErrors).length) {
      setFieldErrors(validationErrors);
      dispatch(setAlert({ message: Object.values(validationErrors)[0], type: "danger" }));
      return;
    }

    setFieldErrors({});
    setSaving(true);

    try {
      const userData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        username: buildUsername(formData.firstName, formData.lastName),
        email: formData.email.trim(),
        designation: formData.designation.trim(),
        gender: formData.gender,
        role: formData.role,
        department: formData.department,
      };

      if (formData.password) {
        userData.password = formData.password;
      }

      if (editingUserId) {
        userData.isActive = formData.isActive;
        await updateUser(editingUserId, userData);
        dispatch(setAlert({ message: "User updated successfully", type: "success" }));
      } else {
        await registerUser(userData);
        dispatch(setAlert({ message: "User registered successfully", type: "success" }));
      }

      closeFormModal();
      loadUsers(currentPage, appliedSearch);
    } catch (error) {
      const apiError =
        error.response?.data?.error ||
        error.response?.data?.errors?.[0] ||
        "Error saving user";
      dispatch(setAlert({ message: apiError, type: "danger" }));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const targetUser = users.find((user) => user._id === deleteUserId);
    if (targetUser && !canManageUser(targetUser)) {
      dispatch(setAlert({
        message: "You cannot delete an administrator account.",
        type: "danger",
      }));
      setShowDeleteModal(false);
      return;
    }

    try {
      await deleteUser(deleteUserId);
      dispatch(setAlert({ message: "User deleted successfully", type: "success" }));
      setShowDeleteModal(false);
      loadUsers(currentPage, appliedSearch);
    } catch (error) {
      dispatch(setAlert({ message: "Error deleting user", type: "danger" }));
    }
  };

  const handleToggleStatus = async (user) => {
    if (!canManageUser(user)) {
      dispatch(setAlert({
        message: "You cannot disable an administrator account.",
        type: "danger",
      }));
      return;
    }

    const nextStatus = user.isActive === false;
    try {
      await toggleUserStatus(user._id, nextStatus);
      dispatch(setAlert({
        message: nextStatus ? "User enabled successfully" : "User disabled successfully",
        type: "success",
      }));
      loadUsers(currentPage, appliedSearch);
    } catch (error) {
      const apiError = error.response?.data?.error || "Error updating user status";
      dispatch(setAlert({ message: apiError, type: "danger" }));
    }
  };

  const getStatusBadge = (user) => {
    if (user.isActive === false) {
      return <span className="badge bg-secondary">Disabled</span>;
    }
    return <span className="badge bg-success">Active</span>;
  };

  const pageStart = totalUsers === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(currentPage * PAGE_SIZE, totalUsers);

  return (
    <div className="container-fluid py-4">
      <div className="card shadow-sm">
        <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <h4 className="mb-0">User Management</h4>
            <small className="text-muted">
              {totalUsers} user{totalUsers === 1 ? "" : "s"} total
              {appliedSearch ? ` matching "${appliedSearch}"` : ""}
            </small>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2 ms-auto">
            <MasterListToolbar
              searchValue={searchInput}
              onSearchChange={(e) => setSearchInput(e.target.value)}
              searchPlaceholder="Search name, email, username, role..."
              searchAriaLabel="Search users"
              onClear={handleClearSearch}
              showClear={Boolean(appliedSearch)}
            />
            <button type="button" className="btn btn-primary btn-sm" onClick={openAddModal}>
              <i className="bi bi-person-plus me-1" aria-hidden="true"></i>
              Add User
            </button>
          </div>
        </div>

        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading</span>
              </div>
            </div>
          ) : visibleUsers.length === 0 ? (
            <div className="text-center text-muted py-5">
              {appliedSearch
                ? <>No users found for <strong>{appliedSearch}</strong>.</>
                : <>No users found. Click <strong>Add User</strong> to register one.</>}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Designation</th>
                    <th>Role</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th className="text-end" style={{ width: "180px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleUsers.map((user) => (
                    <tr key={user._id}>
                      <td>{getDisplayName(user)}</td>
                      <td>{user.username}</td>
                      <td>{user.email}</td>
                      <td>{user.designation || "-"}</td>
                      <td>{user.role?.roleName}</td>
                      <td>{user.department?.departmentName || ""}</td>
                      <td>{getStatusBadge(user)}</td>
                      <td className="text-end">
                        {canManageUser(user) && user._id !== currentUserId ? (
                          <>
                            <button
                              className="btn btn-outline-primary btn-sm me-1"
                              title="Edit User"
                              type="button"
                              onClick={() => openEditModal(user)}
                            >
                              <i className="bi bi-pencil" aria-hidden="true"></i>
                            </button>
                            <button
                              className={`btn btn-sm me-1 ${user.isActive === false ? "btn-outline-success" : "btn-outline-secondary"}`}
                              title={user.isActive === false ? "Enable User" : "Disable User"}
                              type="button"
                              onClick={() => handleToggleStatus(user)}
                            >
                              <i
                                className={`bi ${user.isActive === false ? "bi-person-check" : "bi-person-slash"}`}
                                aria-hidden="true"
                              ></i>
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              title="Delete User"
                              type="button"
                              onClick={() => {
                                setDeleteUserId(user._id);
                                setShowDeleteModal(true);
                              }}
                            >
                              <i className="bi bi-trash" aria-hidden="true"></i>
                            </button>
                          </>
                        ) : (
                          <span className="text-muted small">
                            {user._id === currentUserId ? "Current user" : "-"}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
              <small className="text-muted">
                Showing {pageStart}-{pageEnd} of {totalUsers}
              </small>
              <Pagination className="mb-0">
                <Pagination.Prev
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                />
                {[...Array(totalPages)].map((_, index) => (
                  <Pagination.Item
                    key={index}
                    active={index + 1 === currentPage}
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                />
              </Pagination>
            </div>
          )}
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} size="lg" centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{editingUserId ? "Edit User" : "Add User"}</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit} noValidate>
          <Modal.Body>
            <UserFormFields
              formData={formData}
              fieldErrors={fieldErrors}
              editingUserId={editingUserId}
              roles={roles}
              departments={departments}
              onChange={handleInputChange}
              currentUserId={currentUserId}
            />
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-outline-secondary" onClick={closeFormModal}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : editingUserId ? "Update User" : "Register User"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will soft-delete the user using <strong>isDeleted</strong>. The account will be
          deactivated (<strong>isActive = false</strong>), hidden from this list, and
          <strong>deletedBy</strong> / <strong>deletedAt</strong> will be recorded. The user will
          no longer be able to log in.
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={handleDelete}>
            Delete
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RegisterForm;
